import { data, Form, Link } from 'react-router'
import { resolveDistinctId } from '#app/analytics/attribution.server.ts'
import {
	type DirectoryProps,
	type RouteAnalyticsHandle,
} from '#app/analytics/constants.ts'
import { trackServer } from '#app/analytics/server.ts'
import { BusinessListing } from '#app/components/business-listing.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import {
	getPublishablePages,
	RANKING_ELIGIBLE,
} from '#app/scoring/publishable-pages.server.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { isBotRequest } from '#app/utils/bot.server.ts'
import { recordListingImpressions } from '#app/utils/business-metrics.server.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/search.ts'

/**
 * Site search: /search?q=
 *
 * This is what replaced the header dropdowns. A catalog of ~106 categories
 * across 55 cities and thousands of listings is a search problem rather than a
 * menu problem: a menu has to truncate, and the truncation is invisible to the
 * reader.
 *
 * A plain GET form, so it works without JavaScript and every result is a real
 * link. Deliberately noindex: search result pages are the classic thin-content
 * trap, and this page exists to move a human to the right hub, not to rank.
 */

/** Per result type. Enough to choose from, short enough to scan. */
const RESULT_LIMIT = 8

/** Below this a query matches most of the catalog and helps nobody. */
const MIN_QUERY_LENGTH = 2

export async function loader({ request, context }: Route.LoaderArgs) {
	const query = (new URL(request.url).searchParams.get('q') ?? '').trim()

	if (query.length < MIN_QUERY_LENGTH) {
		return data({
			query,
			categories: [],
			cities: [],
			businesses: [],
			hasSearched: false,
		})
	}

	const contains = { contains: query, mode: 'insensitive' } as const

	const [categories, cities, businesses, publishable] = await Promise.all([
		prisma.category.findMany({
			where: { name: contains, businesses: { some: { status: 'published' } } },
			select: { slug: true, name: true, pluralName: true },
			take: RESULT_LIMIT,
		}),
		prisma.city.findMany({
			where: { name: contains, businesses: { some: { status: 'published' } } },
			select: { slug: true, name: true },
			take: RESULT_LIMIT,
		}),
		prisma.business.findMany({
			where: { name: contains, status: 'published', isClosed: false },
			select: {
				id: true,
				slug: true,
				name: true,
				score: true,
				ratingValue: true,
				ratingCount: true,
				aiDescription: true,
				cities: { take: 1, select: { id: true, name: true, slug: true } },
				categories: { take: 1, select: { id: true, name: true, slug: true } },
			},
			// Scored businesses first: an unscored listing is a weaker answer than
			// one we can actually say something about.
			orderBy: [{ score: { sort: 'desc', nulls: 'last' } }, { name: 'asc' }],
			take: RESULT_LIMIT,
		}),
		getPublishablePages(),
	])

	/*
	 * A category result links to its ranking when one publishes and to its hub
	 * otherwise, matching the 301 the hub itself performs. Sending every result
	 * to /categories/{slug} would route half of them straight into a redirect.
	 */
	const rankingSlugs = new Set(publishable.bestCategories.map((c) => c.slug))

	/*
	 * Internal search is the cheapest demand signal the directory has: it says
	 * what a visitor wanted in their own words, with no ranking lag between
	 * wanting it and us hearing about it. A search that returns nothing is the
	 * most valuable row in the report — it names a category or city the catalog
	 * is missing.
	 *
	 * Emitted server-side so it survives ad blockers and a no-JS document
	 * submit, and bot-filtered for the same reason the counters are.
	 */
	if (!isBotRequest(request)) {
		const distinctId = resolveDistinctId({ request, context })
		const resultCount = categories.length + cities.length + businesses.length
		trackServer({
			distinctId,
			eventName: 'directory_search',
			// Property names come from DirectoryProps so this event and every
			// other discovery event share one shape — a second vocabulary here
			// would fork every dashboard that reads search alongside listings.
			properties: {
				query,
				query_length: query.length,
				page_type: 'search',
				result_count: resultCount,
				has_results: resultCount > 0,
				result_count_by_kind: {
					categories: categories.length,
					cities: cities.length,
					businesses: businesses.length,
				},
			} satisfies DirectoryProps & Record<string, unknown>,
			// One event per visitor per query per minute: React Router refires the
			// loader on revalidation, and a retyped query should not read as
			// renewed demand.
			insertId: distinctId
				? `directory_search:${query}:${distinctId}:${Math.floor(Date.now() / 60_000)}`
				: undefined,
		})

		// Businesses shown in results are impressions, same as any other
		// discovery surface. Never awaited — search must not get slower for it.
		void recordListingImpressions({
			request,
			businessIds: businesses.map((b) => b.id),
			distinctId,
		})
	}

	return data({
		query,
		hasSearched: true,
		categories: categories.map((category) => ({
			...category,
			href: rankingSlugs.has(category.slug)
				? `/best/${category.slug}`
				: `/categories/${category.slug}`,
			ranked: rankingSlugs.has(category.slug),
		})),
		cities,
		businesses,
	})
}

export const handle = {
	analytics: { pageType: 'search' } satisfies RouteAnalyticsHandle,
	breadcrumb() {
		return { name: 'Search', url: '/search' }
	},
	seo({
		loaderData,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		canonical: string
	}): SeoProps {
		const query = loaderData?.query
		return {
			title: query ? `Search: ${query} | DFW Professionals` : 'Search | DFW Professionals',
			description:
				'Search Dallas–Fort Worth businesses, categories and cities scored by DFW Professionals.',
			canonical,
			// Search result pages are thin by construction and near-infinite in
			// number. They are a navigation aid, not an indexable surface.
			robots: 'noindex, follow',
		}
	},
}

function ResultSection<T>({
	title,
	items,
	render,
}: {
	title: string
	items: T[]
	/** `index` is the 0-based position in this section, used for click position. */
	render: (item: T, index: number) => React.ReactNode
}) {
	if (items.length === 0) return null
	return (
		<section className="mb-8">
			<h2 className="mb-3 font-serif text-lg tracking-tight">{title}</h2>
			<ul className="divide-y border-y">{items.map(render)}</ul>
		</section>
	)
}

export default function SearchPage({ loaderData }: Route.ComponentProps) {
	const { query, categories, cities, businesses, hasSearched } = loaderData
	const total = categories.length + cities.length + businesses.length

	return (
		<div className="container mx-auto max-w-3xl px-4 py-12">
			<h1 className="mb-6 font-serif text-3xl tracking-tight md:text-4xl">
				Search
			</h1>

			<Form action="/search" method="GET" role="search" className="mb-10">
				<label htmlFor="search-q" className="sr-only">
					Search businesses, categories and cities
				</label>
				<div className="relative">
					<Icon
						name="magnifying-glass"
						className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2"
					/>
					<input
						id="search-q"
						type="search"
						name="q"
						defaultValue={query}
						autoFocus
						placeholder="A business, category or city"
						className="border-input bg-background focus-visible:ring-ring h-12 w-full rounded-md border py-2 pr-4 pl-10 focus-visible:ring-1 focus-visible:outline-none"
					/>
				</div>
			</Form>

			{/*
			 * Names no category. "Try a category like plumbing or roofing" was two
			 * guesses about a catalog that spans ~106 of them, and on a catalog
			 * seeded without either it advised searching for something that would
			 * return nothing a second time.
			 */}
			{hasSearched && total === 0 ? (
				<p className="text-muted-foreground mb-10 leading-relaxed">
					Nothing matched “{query}”. Search works on category, city and business
					names, so try a broader term, or browse{' '}
					<Link className="underline underline-offset-4" to="/categories">
						all categories
					</Link>{' '}
					or{' '}
					<Link className="underline underline-offset-4" to="/cities">
						all cities
					</Link>
					.
				</p>
			) : null}

			<ResultSection
				title="Categories"
				items={categories}
				render={(category) => (
					<li key={category.slug}>
						<Link
							to={category.href}
							className="hover:bg-muted/50 flex items-center justify-between gap-4 px-1 py-3 transition-colors"
						>
							<span className="font-medium">
								{category.ranked
									? `Best ${categoryPlural(category)} in DFW`
									: categoryPlural(category)}
							</span>
							{category.ranked ? (
								<span className="text-muted-foreground shrink-0 text-xs">
									Ranked
								</span>
							) : null}
						</Link>
					</li>
				)}
			/>

			<ResultSection
				title="Cities"
				items={cities}
				render={(city) => (
					<li key={city.slug}>
						<Link
							to={`/cities/${city.slug}`}
							className="hover:bg-muted/50 block px-1 py-3 font-medium transition-colors"
						>
							{city.name}
						</Link>
					</li>
				)}
			/>

			<ResultSection
				title="Businesses"
				items={businesses}
				render={(business, index) => (
					<li key={business.slug}>
						<BusinessListing
							business={business}
							track={{ pageType: 'search', position: index + 1 }}
						/>
					</li>
				)}
			/>

			<Separator className="my-10" />

			<p className="text-muted-foreground text-sm leading-relaxed">
				Or browse{' '}
				<Link className="underline underline-offset-4" to="/categories">
					every category
				</Link>{' '}
				and{' '}
				<Link className="underline underline-offset-4" to="/cities">
					every DFW city
				</Link>
				.
			</p>
		</div>
	)
}
